<script setup lang="ts">
import type {
	BehaviorConfig,
	MacroregionIdentifier,
	StyleConfig,
	Suggestion,
	SuggestionCondoResult
} from './types';
import type { SuggestionGroupKey } from '~/composables/searchSuggest/types';
import type { SuggestionGroup } from '~/composables/useLocationAutocomplete';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { CurrentPage } from '~/types/mixpanel';

import {
	Command,
	CommandList,
	CommandSeparator
} from '@/components/SearchSuggestRadixCommand';
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger
} from '@/components/ui/tooltip';
import { cn } from '@/utils';
import {
	SECOND_FIELD_GROUP_ORDER,
	SECOND_FIELD_PROVIDERS,
	useGroupRegistry
} from '~/composables/searchSuggest/useGroupRegistry';
import { usePlaceLocation } from '~/composables/searchSuggest/usePlaceLocation';
import { useSuggestCore } from '~/composables/searchSuggest/useSuggestCore';
import { useSuggestDropdown } from '~/composables/searchSuggest/useSuggestDropdown';
import { useSuggestionHandler } from '~/composables/searchSuggest/useSuggestionHandler';
import { useTypingActivity } from '~/composables/searchSuggest/useTypingActivity';
import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import { useSearchSuggestNavigation } from '~/composables/useSearchSuggestNavigation';
import { SEARCH_SUGGEST } from '~/constants';

import ListingEmptyState from '../ListingEmptyState/EmptyState.vue';
import CodeGroup from './CodeGroup.vue';
import { getMacroregions } from './constants';
import InsideLabelSuggestInput from './InsideLabelSuggestInput.vue';
import LoadingGroup from './LoadingGroup.vue';
import NewGroup from './NewGroup.vue';

interface SecondFieldSuggestProps {
	// Configurações de estilo
	styleConfig?: StyleConfig;
	// Configurações de comportamento
	behaviorConfig?: BehaviorConfig;
	// Props específicas do segundo campo
	disabled?: boolean;
	hasFirstFieldValue?: boolean;
	firstFieldSearchTerm?: string;
	city?: string;
	state?: string;
	// Props individuais mantidas para compatibilidade (deprecated)
	searchTerm?: string;
	isFetchingPropertyByCodeSearch?: boolean;
	currentPage?: CurrentPage;
	// Optional new props (non-breaking)
	providers?: Array<
		| 'cities'
		| 'macroregions'
		| 'regions'
		| 'streets'
		| 'points_of_interest'
		| 'condos'
	>;
	groupOrder?: SuggestionGroupKey[];
}

const props = withDefaults(defineProps<SecondFieldSuggestProps>(), {
	disabled: false,
	hasFirstFieldValue: false,
	firstFieldSearchTerm: '',
	isFetchingPropertyByCodeSearch: false,
	styleConfig: () => ({}),
	behaviorConfig: () => ({
		enableAnimation: true,
		closeOnClickOutside: true,
		closeOnSelect: true,
		showCloseIcon: false
	}),
	providers: () => SECOND_FIELD_PROVIDERS,
	groupOrder: () => SECOND_FIELD_GROUP_ORDER
});

const emit = defineEmits<{
	'select-neighborhood': [event: SelectAddressEvent];
	'select-street': [event: SelectAddressEvent];
	'select-poi': [event: SelectAddressEvent];
	'select-condo': [value: SuggestionCondoResult];
	'select-code': [value: Suggestion['searchParams']];
	'select-history': [value: Suggestion, isEmptyState: boolean];
	'update:search-term': [value: string];
	'close-modal': [];
}>();

const isDropdownOpened = defineModel<boolean>('isDropdownOpened', {
	default: false
});

// Template refs
const selectedValue = ref<SuggestionGroup>();
const commandListEl = useTemplateRef<HTMLElement>('commandListEl');
const inputEl =
	useTemplateRef<InstanceType<typeof InsideLabelSuggestInput>>('inputEl');
const commandEl = useTemplateRef<HTMLElement>('commandEl');

// Merge style config with disabled state styling
const mergedStyle = computed<StyleConfig>(() => ({
	...props.styleConfig,
	inputWrapperClasses: cn(
		props.styleConfig?.inputWrapperClasses,
		props.hasFirstFieldValue && !props.disabled && 'bg-white'
	)
}));

// Internal state
const searchTerm = shallowRef<string>(props.searchTerm || '');
const modelValue = ref<Suggestion>();
const hasSelectedValue = shallowRef<boolean>(false);
const isNavigatingWithKeyboard = ref<boolean>(false);

// Core search logic with macroregion bounding
const {
	hasMinSearchTerm,
	locationAutocompleteData,
	loading,
	searchByCodeOption,
	executeLocationAutocomplete
} = useSuggestCore({
	searchTerm,
	providers: props.providers,
	hasSelectedValue,
	isDropdownOpen: isDropdownOpened
});

// Research history
const { filteredLastResearchs, updateResearchs, removeResearchs } =
	useResearchsSuggest();

// Group registry with field-specific providers
const registry = useGroupRegistry(props.providers);

// Typing activity detection
const { isTyping } = useTypingActivity(
	searchTerm,
	SEARCH_SUGGEST.TYPING_DEBOUNCE_MS,
	hasSelectedValue
);

// Place location fetcher
const { getLocation } = usePlaceLocation();

// Dropdown management
const dropdown = useSuggestDropdown({
	behaviorConfig: props.behaviorConfig,
	commandEl,
	searchTerm,
	hasSelectedValue,
	onCloseModal: () => emit('close-modal')
});

// Bind dropdown state to model
watch(
	dropdown.isOpen,
	value => {
		isDropdownOpened.value = value;
	},
	{ immediate: true }
);

watch(isDropdownOpened, value => {
	if (value !== dropdown.isOpen.value) {
		if (value) {
			dropdown.open();
		} else {
			dropdown.close();
		}
	}
});

// Navigation
const {
	handleSelectCode: navigateToCode,
	isFetchingPropertyByCodeSearch: internalIsFetchingPropertyByCodeSearch
} = useSearchSuggestNavigation({ currentPage: props.currentPage });

// Combined loading state
const isFetchingByCode = computed(
	() =>
		props.isFetchingPropertyByCodeSearch ||
		internalIsFetchingPropertyByCodeSearch.value
);

// Show loading only when user has typed enough characters and is loading/typing
const isLoadingOrTyping = computed<boolean>(() => {
	return hasMinSearchTerm.value && (loading.value || isTyping.value);
});

// Visible groups using registry
const visibleGroups = computed(() => {
	if (props.disabled) return [];

	return registry.getVisibleGroups(
		{
			state: {
				searchTerm,
				loading,
				isDropdownOpened,
				hasSelectedValue,
				locationAutocompleteData,
				filteredLastResearchs,
				searchByCodeOption,
				isTyping,
				disabled: toRef(() => props.disabled)
			},
			searchTerm: searchTerm.value,
			hasMinSearchTerm: hasMinSearchTerm.value
		},
		props.groupOrder
	);
});

// Empty state detection - "No results found"
const shouldShowNoResultsEmptyState = computed<boolean>(() => {
	return (
		!props.disabled &&
		hasMinSearchTerm.value &&
		!loading.value &&
		!isTyping.value &&
		dropdown.isOpen.value &&
		(!locationAutocompleteData.value ||
			locationAutocompleteData.value.length === 0)
	);
});

// Suggestion handler with strategy pattern (must be after shouldShowNoResultsEmptyState)
const { handleSuggestion, getSelectText } = useSuggestionHandler(
	{
		emitHistory: (suggestion, isEmptyState) =>
			emit('select-history', suggestion, isEmptyState),
		emitCode: searchParams => emit('select-code', searchParams),
		emitNeighborhood: event => emit('select-neighborhood', event),
		emitStreet: event => emit('select-street', event),
		emitPoi: event => emit('select-poi', event),
		emitCondo: value => emit('select-condo', value)
	},
	getLocation,
	shouldShowNoResultsEmptyState
);

// Consolidated suggestion status check - single iteration for better performance
const suggestionStatus = computed(() => {
	const status = { hasSearch: false, hasHistory: false };

	for (const group of visibleGroups.value) {
		if (group.suggestions?.length > 0) {
			if (group.key === 'lastResearchs') {
				status.hasHistory = true;
			} else {
				status.hasSearch = true;
			}
		}

		// Early exit if both are found
		if (status.hasSearch && status.hasHistory) break;
	}

	return status;
});

// Instructional state - "Find places in..."
const shouldShowInstructionalState = computed<boolean>(() => {
	return (
		!props.disabled &&
		!hasMinSearchTerm.value &&
		!loading.value &&
		dropdown.isOpen.value &&
		!suggestionStatus.value.hasSearch
	);
});

const hasHistorySuggestions = computed<boolean>(
	() => suggestionStatus.value.hasHistory
);

// Placeholders
const placeholderClosed = 'Bairros, endereços e condomínios';
const placeholderOpen = 'Bairros, endereços, condomínios e código do imóvel';

// Use mergedStyle directly to avoid unnecessary recomputation wrappers

// Tooltip should only be enabled when disabled
const isTooltipEnabled = computed<boolean>(() => !props.disabled);

// Get macroregion metadata for instructional stat

const router = useRouter();

/**
 * Get display text for a suggestion
 */
function getDisplayValue(
	value: string | number | boolean | Record<string, any>
): string {
	const suggestion = value as Suggestion;

	const { currentRoute } = router;

	const isSRP =
		currentRoute.value.path.includes('/venda/imoveis') ||
		currentRoute.value.path.includes('/venda/mapa-de-imoveis');

	// Show only subtitle for regions on SRP
	if (suggestion.searchType === 'region' && isSRP) {
		return suggestion.result.subtitle;
	}

	return getSelectText(suggestion);
}

/**
 * Watch external search term prop
 */
watch(
	() => props.searchTerm,
	value => {
		if (value && value.trim()) {
			searchTerm.value = value;
		} else if (value === undefined || value === null) {
			searchTerm.value = '';
		}
	},
	{ immediate: true }
);

/**
 * Emit search term updates
 */
watch(searchTerm, newValue => {
	emit('update:search-term', newValue);
});

/**
 * Handle input focus
 */
function handleInputFocus() {
	if (props.disabled) return;

	dropdown.setUserFocusedOpening(true);
	dropdown.open();

	if (isNavigatingWithKeyboard.value) return;

	if (
		!searchTerm.value ||
		searchTerm.value.length < SEARCH_SUGGEST.MIN_SEARCH_LENGTH
	) {
		// Don't execute if not enough characters
	} else {
		executeLocationAutocomplete();
	}
}

/**
 * Handle model value update (selection)
 * Delegates to strategy-based handler for cleaner code
 */
async function handleModelValueUpdate(suggestion: Suggestion) {
	hasSelectedValue.value = true;

	await handleSuggestion(suggestion, {
		searchTerm,
		closeOnSelect: props.behaviorConfig?.closeOnSelect,
		city: props.city,
		state: props.state,
		onClose: () => dropdown.close(),
		onUpdateResearch: updateResearchs
	});
}

/**
 * Handle input clear
 */
function handleInputClear() {
	searchTerm.value = '';
	modelValue.value = undefined;
	hasSelectedValue.value = false;
	focusInput();
	dropdown.open();
}

/**
 * Handle enter key
 */
function handleEnter(
	_: KeyboardEvent,
	selectedElement: HTMLElement | undefined
) {
	if (
		(shouldShowNoResultsEmptyState.value ||
			shouldShowInstructionalState.value) &&
		hasMinSearchTerm.value &&
		!isTyping.value
	) {
		searchTerm.value = '';
		hasSelectedValue.value = false;
		return;
	}

	selectedElement?.click();
	inputEl.value?.blur();
}

/**
 * Handle keyboard navigation
 */
function handleKeyDown(e: KeyboardEvent) {
	isNavigatingWithKeyboard.value = true;
	dropdown.setNavigatingWithKeyboard(true);
}

/**
 * Handle close modal
 */
function handleCloseModal() {
	emit('close-modal');
}

/**
 * Focus input
 */
function focusInput() {
	if (!props.disabled) {
		inputEl.value?.focus();
	}
}

// Set placeholders
dropdown.setPlaceholders(placeholderClosed, placeholderOpen);
</script>

<template>
	<TooltipProvider
		:disabled="isTooltipEnabled"
		disable-closing-trigger
		:delay-duration="100"
	>
		<div ref="commandEl" class="relative">
			<Tooltip>
				<TooltipTrigger as-child>
					<Command
						v-model="modelValue"
						v-model:search-term="searchTerm"
						v-model:selected-value="selectedValue"
						:display-value="getDisplayValue"
						:open="dropdown.isOpen.value"
						:class="cn('h-auto w-full border-0', mergedStyle.class)"
						:disabled="disabled"
						@update:model-value="(value: any) => handleModelValueUpdate(value)"
						@keydown="handleKeyDown"
					>
						<InsideLabelSuggestInput
							ref="inputEl"
							:placeholder="placeholderClosed"
							:open-input-placeholder="placeholderOpen"
							:wrapper-classes="mergedStyle.inputWrapperClasses"
							:class="
								cn(
									'h-[62px] text-ellipsis pl-4 text-primary disabled:bg-white-dark-2 disabled:text-primary/40',
									mergedStyle.inputClasses
								)
							"
							:auto-focus="false"
							:input-clear-icon-wrapper-classes="
								mergedStyle.inputClearIconWrapperClasses
							"
							:input-clear-icon-classes="mergedStyle.inputClearIconClasses"
							:input-search-icon-wrapper-classes="
								cn('hidden', mergedStyle.inputSearchIconWrapperClasses)
							"
							:input-search-icon-classes="mergedStyle.inputSearchIconClasses"
							:model-value="searchTerm"
							:enable-animation="behaviorConfig.enableAnimation"
							:show-close-icon="behaviorConfig.showCloseIcon"
							:disabled="disabled"
							@focus.prevent="handleInputFocus"
							@clear="handleInputClear"
							@close-modal="handleCloseModal"
							@enter="handleEnter"
						>
							<template #prefix>
								<slot name="input-prefix" />
							</template>
							<template #suffix>
								<div
									:class="
										cn(
											'flex h-full w-7 mr-3 items-center justify-end',
											mergedStyle.inputClearIconWrapperClasses
										)
									"
								>
									<IconMaterialSymbolsLightCancel
										v-show="modelValue && !disabled"
										:class="
											cn(
												'size-5 cursor-pointer text-white-dark-3',
												mergedStyle.inputClearIconClasses
											)
										"
										@click="() => handleInputClear()"
									/>
								</div>
							</template>
						</InsideLabelSuggestInput>

						<CommandList
							v-if="!disabled"
							ref="commandListEl"
							:class="
								cn(
									'absolute top-full left-0 right-0 z-50 h-auto max-h-[400px] border border-white-dark-3 border-t bg-white p-0',
									{
										'overflow-hidden': isLoadingOrTyping
									},
									mergedStyle.listWrapperClasses
								)
							"
						>
							<!-- No results empty state -->
							<div
								v-show="shouldShowNoResultsEmptyState && !isTyping"
								:class="cn('px-5 py-6', mergedStyle.emptyStateWrapperClasses)"
							>
								<ListingEmptyState
									:show-badges="false"
									:icon-wrapper-classes="
										cn(
											'size-[47.5px]',
											mergedStyle.emptyStateIconWrapperClasses
										)
									"
									:icon-classes="
										cn('size-[22.5px]', mergedStyle.emptyStateIconClasses)
									"
									:title-classes="
										cn('text-lg', mergedStyle.emptyStateTitleClasses)
									"
									:description-classes="
										cn('text-sm', mergedStyle.emptyStateDescriptionClasses)
									"
								>
									<template #title>Nenhum resultado encontrado</template>
									<template #description>
										Tente uma busca diferente ou verifique a grafia
									</template>
								</ListingEmptyState>
							</div>

							<!-- Instructional empty state -->
							<div
								v-show="shouldShowInstructionalState"
								:class="
									cn(
										'flex flex-col gap-1 items-center justify-center px-5 text-center py-8',
										!hasHistorySuggestions && 'h-full',
										mergedStyle.emptyStateWrapperClasses
									)
								"
							>
								<h3
									:class="
										cn(
											'text-base font-normal text-primary-light-3 leading-[150%]',
											mergedStyle.emptyStateTitleClasses
										)
									"
								>
									Encontre lugares em
									{{ firstFieldSearchTerm || 'sua região' }}
								</h3>
								<p
									:class="
										cn(
											'font-Inter text-sm text-primary-light-3 leading-[150%]',
											mergedStyle.emptyStateDescriptionClasses
										)
									"
								>
									Digite bairro, endereço, condomínio ou código do imóvel para
									ver sugestões
								</p>
							</div>

							<LoadingGroup
								v-for="index in 3"
								:key="`loading-group-${index}`"
								:group-heading-classes="mergedStyle.groupHeadingClasses"
								:group-icon-classes="mergedStyle.groupIconClasses"
								:group-item-wrapper-classes="
									mergedStyle.groupItemWrapperClasses
								"
								:group-item-text-classes="mergedStyle.groupItemTextClasses"
								:group-item-sub-text-classes="
									mergedStyle.groupItemSubTextClasses
								"
								:active="isLoadingOrTyping"
							/>

							<template v-if="!isLoadingOrTyping && visibleGroups.length">
								<template
									v-for="(suggestionGroup, index) in visibleGroups"
									:key="`suggestion-group-${suggestionGroup.key}`"
								>
									<CommandSeparator
										class="bg-white-dark-2"
										:class="{ hidden: index === 0 }"
									/>

									<!-- History group (only when not showing no results) -->
									<NewGroup
										v-if="
											suggestionGroup.key === 'lastResearchs' &&
											!shouldShowNoResultsEmptyState
										"
										:suggestion-key="'lastResearchs'"
										:heading="suggestionGroup.heading"
										:suggestions="suggestionGroup.suggestions"
										:search-term="searchTerm"
										is-removable
										:class="mergedStyle.groupWrapperClasses"
										:group-heading-classes="mergedStyle.groupHeadingClasses"
										:group-icon-classes="mergedStyle.groupIconClasses"
										:group-item-wrapper-classes="
											mergedStyle.groupItemWrapperClasses
										"
										:group-item-text-classes="mergedStyle.groupItemTextClasses"
										:group-item-sub-text-classes="
											mergedStyle.groupItemSubTextClasses
										"
										@remove-suggestion="removeResearchs"
									/>

									<!-- Other suggestion groups -->
									<NewGroup
										v-else-if="suggestionGroup.key !== 'code'"
										:suggestion-key="suggestionGroup.key"
										:heading="suggestionGroup.heading"
										:suggestions="suggestionGroup.suggestions"
										:search-term="searchTerm"
										:class="mergedStyle.groupWrapperClasses"
										:group-heading-classes="mergedStyle.groupHeadingClasses"
										:group-icon-classes="mergedStyle.groupIconClasses"
										:group-item-wrapper-classes="
											mergedStyle.groupItemWrapperClasses
										"
										:group-item-text-classes="mergedStyle.groupItemTextClasses"
										:group-item-sub-text-classes="
											mergedStyle.groupItemSubTextClasses
										"
									/>

									<!-- Code group -->
									<CodeGroup
										v-else-if="(suggestionGroup.key as any) === 'code'"
										:search-term="searchTerm"
										suggestion-key="code"
										:suggestions="suggestionGroup.suggestions as any"
										:is-fetching-property-by-code-search="isFetchingByCode"
										:class="mergedStyle.groupWrapperClasses"
										:group-heading-classes="mergedStyle.groupHeadingClasses"
										:group-icon-classes="mergedStyle.groupIconClasses"
										:group-item-wrapper-classes="
											mergedStyle.groupItemWrapperClasses
										"
										:group-item-text-classes="mergedStyle.groupItemTextClasses"
										:group-item-sub-text-classes="
											mergedStyle.groupItemSubTextClasses
										"
									/>
								</template>
							</template>
						</CommandList>
					</Command>
				</TooltipTrigger>
				<TooltipContent
					side="bottom"
					align="center"
					class="relative overflow-visible rounded-xs bg-primary-light-4 p-3 font-sans text-xs font-normal leading-normal text-white before:absolute before:left-1/2 before:top-0 before:z-100 before:size-1.5 before:-translate-y-1/2 before:translate-x-1/2 before:rotate-45 before:bg-inherit before:content-['']"
					:side-offset="-7"
				>
					Selecione primeiro uma cidade ou região
				</TooltipContent>
			</Tooltip>
		</div>
	</TooltipProvider>
</template>
